<template>
  <Form
    as="v-form"
    @submit="validateForm"
    :validation-schema="formSchema"
    v-slot="{ errors }"
  >
    <!-- Form fields -->
    <v-col
      v-for="(formField, index) in visibleFormFields"
      :key="index"
      :class="getFieldPadding(formField)"
      cols="12"
      >
        <Field
          v-model="personalData[formField.name]"
          :name="formField.name"
          v-slot="{ field }"
          :rules="formField.rules"
        >
            <VTextField
              v-bind="field"
              :label="t('formFields.' + formField.name)"
              @input="isFormValid = true"
              :helper-text="errors[formField.name]"
              :invalid="errors.hasOwnProperty(`${ formField.name }`)"
              :max-length="formField.maxLength"
              :counter="formField.value.length > formField.maxLength - 5"
              aria-labelledby="heading"
            />
        </Field>
    </v-col>

    <!-- Form action buttons -->
    <v-row class="ma-0 d-flex" :justify="getActionsRowAlignment">
      <scale-button
        id="secondaryBtn"
          v-if="actualScreen != 'successScreen'"
        type="button"
        variant="informational"
        @click="handleSecondaryBtnClick()"
        class="mr-2"
      >
        {{ t("text." + actualScreen + ".secondaryBtn") }}
      </scale-button>

      <scale-button
        id="primaryBtn"
        type="submit"
        :disabled="apiRequestInProgress || !isFormValid"
      >
        <!-- Loading spinner -->
        <scale-loading-spinner
          v-if="apiRequestInProgress"
          size="small"
        ></scale-loading-spinner>
        <span v-else>
          {{ t("text." + actualScreen + ".primaryBtn") }}
        </span>
      </scale-button>
    </v-row>
  </Form>
</template>

<script setup>
import { ref, computed, defineEmits, defineProps, inject, reactive } from 'vue';
import VTextField from './VTextField.vue';
import PersonAPI from '../services/PersonAPI';

import { Form, Field } from 'vee-validate';
import { useI18n } from 'vue-i18n'

const { t } = useI18n() 
const keycloak = inject('keycloak')

// Emits
const emit = defineEmits(['screen-change', 'email-updated', 'notify'])

const props = defineProps({
  actualScreen: {
    type: String,
    required: true,
  },
});

const personalData = reactive({
  email: "",
  confirmEmail: "",
  pinCode: ""
});

const formSchema = computed(() => {
  const schema = {};

  formFields.value.forEach(field => {
    if (field.rules && !field.readonly && field.isVisible) {
      if(field.name === "confirmEmail") {
        schema[field.name] = field.rules + personalData.email
      } else {
        schema[field.name] = field.rules;
      }
    }
  });

  return schema;
})

// Reactive state variables
const isFormValid = ref(true);
const apiRequestInProgress = ref(false);

const formFields = computed(() => {
  return [
  {
    name: 'email',
    value: '',
    isVisible: props.actualScreen === "emailScreen",
    maxLength: 241,
    rules: 'required|max:241|email|not_internal_email',
  },
  {
    name: 'confirmEmail',
    value: '',
    isVisible: props.actualScreen === "emailScreen",
    maxLength: 241,
    rules: `required|max:241|match:`,
  },
  {
    name: 'pinCode',
    value: '',
    isVisible: props.actualScreen === "verificationScreen",
    maxLength: 6,
    rules: 'required|length:6',
  },
]
});

const visibleFormFields = computed(() => {
  return formFields.value.filter((field) => field.isVisible);
});

const getActionsRowAlignment = computed(() => {
  switch (props.actualScreen) {
    case 'emailScreen':
      return 'space-between';
    case 'successScreen':
      return 'center';
    default:
      return 'end';
  }
});

// Methods
const getFieldPadding = (field) => {
  return field.value && field.value.length > field.maxLength - 5 ? 'pa-0 pb-3' : 'pa-0 pb-8';
};

const validateForm = () => {
  switch (props.actualScreen) {
    case 'emailScreen':
      validateEmailForm();
      break;
    case 'verificationScreen':
      verifyCode();
      break;
    case 'successScreen':
      emit('screen-change', 'mainScreen');
      break;
    default:
      break;
  }
};

const handleSecondaryBtnClick = () => {
  switch (props.actualScreen) {
    case 'emailScreen':
      emit('screen-change', 'mainScreen');
      break;
    case 'verificationScreen':
      requestEmailUpdate();
      break;
    default:
      break;
  }
};

const validateEmailForm = () => {
  isFormValid.value = true;
  requestEmailUpdate();
};

const requestEmailUpdate = () => {
  apiRequestInProgress.value = true;
  PersonAPI.requestEmailUpdate(keycloak.token, personalData.email)
    .then(() => {
      apiRequestInProgress.value = false;
      isFormValid.value = true;
      emit('screen-change', 'verificationScreen');
      emit('notify', 'informational', t('notificationMsg.emailChange.title'), t('notificationMsg.emailChange.text'));
    })
    .catch((err) => {
      apiRequestInProgress.value = false;
      emit('notify', 'danger', t('notificationMsg.emailChange.error'), err.response.data.error);
      console.error('Error requesting email change -> ', err);
    });
};

const verifyCode = () => {
  isFormValid.value = true;
      apiRequestInProgress.value = true;
      PersonAPI.verifyCode(keycloak.token, personalData.email, personalData.pinCode)
        .then((res) => {
          apiRequestInProgress.value = false;
          emit('email-updated', res.data.sessionId);
        })
        .catch((err) => {
          apiRequestInProgress.value = false;
          let errMsg = err.response.data.error;
          if (!errMsg) {
            errMsg = err;
          }
          emit('notify', 'danger', t('notificationMsg.emailChange.error'), errMsg);
          console.error('Error verifying code -> ', err);
        });
};
</script>
